import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideRouter } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { appRoutes } from './app.routes';
import {
  provideAuthServiceFe,
  provideLogServiceFe,
  providePictureServiceFe,
  traceIdInterceptor,
} from '@erbsen/shared/data-access';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeuix/themes';
import Lara from '@primeuix/themes/lara';

// Viva surface palette (consistent with sakai-showcase config)
const ErbsenPreset = definePreset(Lara, {
  semantic: {
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '#f3f3f3',
          100: '#e7e7e8',
          200: '#cfd0d0',
          300: '#b7b8b9',
          400: '#9fa1a1',
          500: '#87898a',
          600: '#6e7173',
          700: '#565a5b',
          800: '#3e4244',
          900: '#262b2c',
          950: '#0e1315',
        },
      },
      dark: {
        surface: {
          0: '#ffffff',
          50: '#f3f3f3',
          100: '#e7e7e8',
          200: '#cfd0d0',
          300: '#b7b8b9',
          400: '#9fa1a1',
          500: '#87898a',
          600: '#6e7173',
          700: '#565a5b',
          800: '#3e4244',
          900: '#262b2c',
          950: '#0e1315',
        },
      },
    },
  },
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideAnimationsAsync(),
    provideRouter(appRoutes),
    provideHttpClient(withInterceptors([traceIdInterceptor])),
    provideLogServiceFe({
      serviceName: 'user-shell',
      endpoint: '/ingest',
    }),
    provideAuthServiceFe({
      endpoint: '/user-api',
    }),
    providePictureServiceFe({
      endpoint: '/image-api',
    }),
    providePrimeNG({
      theme: {
        preset: ErbsenPreset,
        options: {
          darkModeSelector: '.app-dark',
          cssLayer: {
            name: 'primeng',
            order: 'tailwind-base, primeng, tailwind-utilities',
          },
        },
      },
      translation: {
        startsWith: 'Beginnt mit',
        contains: 'Enthält',
        notContains: 'Enthält nicht',
        endsWith: 'Endet mit',
        equals: 'Gleich',
        notEquals: 'Nicht gleich',
        noFilter: 'Kein Filter',
        lt: 'Kleiner als',
        lte: 'Kleiner oder gleich',
        gt: 'Größer als',
        gte: 'Größer oder gleich',
        is: 'Ist',
        isNot: 'Ist nicht',
        before: 'Vor',
        after: 'Nach',
        dateIs: 'Datum ist',
        dateIsNot: 'Datum ist nicht',
        dateBefore: 'Datum vor',
        dateAfter: 'Datum nach',
        clear: 'Löschen',
        apply: 'Anwenden',
        matchAll: 'Alle erfüllen',
        matchAny: 'Eines erfüllen',
        addRule: 'Regel hinzufügen',
        removeRule: 'Regel entfernen',
        accept: 'Ja',
        reject: 'Nein',
        choose: 'Auswählen',
        upload: 'Hochladen',
        cancel: 'Abbrechen',
        dayNames: [
          'Sonntag',
          'Montag',
          'Dienstag',
          'Mittwoch',
          'Donnerstag',
          'Freitag',
          'Samstag',
        ],
        dayNamesShort: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
        dayNamesMin: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
        monthNames: [
          'Januar',
          'Februar',
          'März',
          'April',
          'Mai',
          'Juni',
          'Juli',
          'August',
          'September',
          'Oktober',
          'November',
          'Dezember',
        ],
        monthNamesShort: [
          'Jan',
          'Feb',
          'Mär',
          'Apr',
          'Mai',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Okt',
          'Nov',
          'Dez',
        ],
        today: 'Heute',
        weekHeader: 'Woche',
        weak: 'Schwach',
        medium: 'Mittel',
        strong: 'Stark',
        passwordPrompt: 'Passwort eingeben',
        emptyMessage: 'Keine Optionen verfügbar',
        emptyFilterMessage: 'Keine Ergebnisse gefunden',
        aria: {
          trueLabel: 'Wahr',
          falseLabel: 'Falsch',
          nullLabel: 'Nicht ausgewählt',
          pageLabel: 'Seite {page}',
          firstPageLabel: 'Erste Seite',
          lastPageLabel: 'Letzte Seite',
          nextPageLabel: 'Nächste Seite',
          previousPageLabel: 'Vorherige Seite',
        },
      },
    }),
  ],
};
